import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import _ from "lodash";
import CardTelegramLink from "./cardTelegramLink";
import DialogChangePassword from "./dialogChangePassword";
import SelectorCompany from "../selectorCompany";
import CompanyLogoPicker from "../companyLogoPicker";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    marginLeft: "21.5rem",
    paddingBottom: "1rem",
    backgroundColor: "#404040",
  },
  content: {
    flexGrow: 1,
    backgroundColor: "#404040",
    color: "white",
    align: "center",
    textAlign: "center",
    marginTop: "0.5%",
    marginRight: "1rem",
  },
  accountInfoWrapper: {
    padding: "2rem 2rem 2rem 2rem",
  },
  accountInfo: {
    display: "flex",
    justifyContent: "flex-start",
    margin: "2rem 0rem 2rem 0rem",
  },
  card: {
    margin: "0rem 0rem 0.5rem 0.5rem",
  },
  titles: {
    width: "33%",
    float: "left",
    margin: "0rem 0rem 0rem 0rem",
  },
  titleText: {
    margin: "0rem 0rem 0.5rem 0rem",
  },
  companySwitch: {
    display: "flex",
    justifyContent: "flex-start",
    textAlign: "left",
  },
  companyLogo: {
    display: "flex",
    justifyContent: "flex-start",
    textAlign: "left",
    marginBottom: "0.75rem",
  },
  cardTitleText: {
    fontSize: 26,
    fontWeight: 540,
  },
}));

export default function accountInformation(props) {
  const [isChangePasswordDialogOn, setIsChangePasswordDialogOn] = useState(false);
  // Shown straight after an upload, so the panel does not keep the old logo
  // until the next page load.
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState(null);

  const classes = useStyles();

  // Changing the company's own logo is an admin job; a member only sees it.
  const canEditCompany = props.userRole === "companyAdmin" || props.userRole === "superAdmin";
  const companyID = _.get(props, "userCompany._id");
  const logoUrl = uploadedLogoUrl || _.get(props, "userCompany.companyLogo");

  // A company can have no subscription at all — one created before the server
  // started building one, or seeded by hand. Reading periods[last] straight off
  // it took this whole panel down with a TypeError.
  const periods = _.get(props, "userCompany.subscription.periods");
  const currentPeriod = Array.isArray(periods) && periods.length > 0 ? periods[periods.length - 1] : null;
  const currentPlan = _.get(props, "userCompany.subscription.currentPlan");
  const planRecord = currentPlan ? props.plans[props.getPlanIndex(currentPlan)] : null;
  const NOT_SET = "—";

  return (
    <div className={classes.root} id="AccountInformation">
      <main className={classes.content}>
        <Card className={classes.card}>
          <div className={classes.accountInfoWrapper}>
            <Typography className={classes.cardTitleText} align="left">
              Account Info
            </Typography>
            <div className={classes.accountInfo}>
              <div className={classes.titles}>
                <Typography className={classes.titleText} variant="h6" align="left">
                  Company
                </Typography>
                <Typography align="left" gutterBottom>
                  {_.get(props, "userCompany.name")}
                </Typography>
                {canEditCompany && companyID && (
                  <div className={classes.companyLogo}>
                    <CompanyLogoPicker
                      inputId="accountCompanyLogoInput"
                      companyID={companyID}
                      logoUrl={logoUrl}
                      createSnackbar={props.createSnackbar}
                      onUploaded={setUploadedLogoUrl}
                    />
                  </div>
                )}
                {/* Renders nothing unless this user has somewhere to switch to. */}
                <div className={classes.companySwitch}>
                  <SelectorCompany
                    switchTargets={props.switchTargets}
                    currentCompanyID={localStorage.getItem("user.companyID")}
                    createSnackbar={props.createSnackbar}
                  />
                </div>
              </div>
              <div className={classes.titles}>
                <Typography className={classes.titleText} variant="h6" align="left">
                  Username
                </Typography>
                <Typography align="left" gutterBottom>
                  {_.get(props, "userName")}
                </Typography>
                <Button
                  color={"primary"}
                  style={{
                    margin: "0 0 1rem 0",
                    padding: "0",
                    float: "left",
                    textTransform: "none",
                    textDecorationLine: "underline",
                  }}
                  onClick={() => {
                    setIsChangePasswordDialogOn(true);
                  }}
                >
                  Change Password
                </Button>
                <DialogChangePassword
                  isOpen={isChangePasswordDialogOn}
                  onClose={() => {
                    setIsChangePasswordDialogOn(false);
                  }}
                  createSnackbar={props.createSnackbar}
                ></DialogChangePassword>
              </div>
              <div className={classes.titles}>
                <Typography className={classes.titleText} variant="h6" align="left">
                  Current Plan
                </Typography>
                <Typography align="left" gutterBottom>
                  {currentPlan || NOT_SET}
                </Typography>
              </div>
            </div>
          </div>
        </Card>
        {/* Renders nothing where no bot is configured. */}
        <CardTelegramLink createSnackbar={props.createSnackbar} />
        <Card className={classes.card}>
          <div className={classes.accountInfoWrapper}>
            <Typography className={classes.cardTitleText} align="left">
              Playables
            </Typography>
            <div className={classes.accountInfo}>
              <div className={classes.titles}>
                <Typography className={classes.titleText} variant="h6" align="left">
                  Active Playable Count
                </Typography>
                <Typography align="left" gutterBottom>
                  {_.get(props, "numberOfBases")}
                </Typography>
              </div>
              <div className={classes.titles}>
                <Typography className={classes.titleText} variant="h6" align="left">
                  Monthly Downloads
                </Typography>
                <Typography align="left" gutterBottom>
                  {currentPeriod ? currentPeriod.monthlyCreatedPlayables : NOT_SET}
                </Typography>
              </div>
              <div className={classes.titles}>
                <Typography className={classes.titleText} variant="h6" align="left">
                  Monthly Download Limit
                </Typography>
                <Typography align="left" gutterBottom>
                  {_.get(planRecord, "numberOfPlayables", NOT_SET)}
                </Typography>
              </div>
            </div>
          </div>
        </Card>
        <Card className={classes.card}>
          <div className={classes.accountInfoWrapper}>
            <Typography className={classes.cardTitleText} align="left">
              Videos
            </Typography>
            <div className={classes.accountInfo}>
              <div className={classes.titles}>
                <Typography className={classes.titleText} variant="h6" align="left">
                  Monthly Downloads
                </Typography>
                <Typography align="left" gutterBottom>
                  {currentPeriod ? currentPeriod.monthlyCreatedVideos : NOT_SET}
                </Typography>
              </div>
              <div className={classes.titles}>
                <Typography className={classes.titleText} variant="h6" align="left">
                  Monthly Download Limit
                </Typography>
                <Typography align="left" gutterBottom>
                  {_.get(planRecord, "numberOfVideos", NOT_SET)}
                </Typography>
              </div>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}
